import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, { signal });
    document.addEventListener('visibilitychange', this.#syncPlayback, { signal });
    this.#visibilityObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target !== this.#visibilityTarget) continue;
        this.#selectedVisible = entry.isIntersecting && entry.intersectionRect.width > 0 && entry.intersectionRect.height > 0;
      }
      this.#syncPlayback();
    }, { threshold: [0, 0.01] });
    this.#gridObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) this.#gridVisibility.set(entry.target, entry.isIntersecting && entry.intersectionRect.width > 0 && entry.intersectionRect.height > 0);
      this.#syncPlayback();
    }, { threshold: [0, 0.01] });
    Promise.all([customElements.whenDefined('slideshow-component'), customElements.whenDefined('deferred-media')]).then(() => {
      if (!this.isConnected || signal.aborted) return;
      for (const media of this.#galleryVideos()) {
        if (media.closest('.media-gallery__grid')) this.#gridObserver?.observe(media);
      }
      if (this.#selectedSlide) this.#syncPlayback();
      else this.#selectPlaybackSlide(this.slideshow?.slides?.[this.slideshow.current]);
    });

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #requestId = 0;
  #visibilityObserver;
  #visibilityTarget;
  #gridObserver;
  #gridVisibility = new WeakMap();
  #selectedSlide;
  #selectedVisible = false;

  #galleryVideos() {
    return [...this.querySelectorAll('deferred-media')]
      .filter((media) => media.closest('media-gallery') === this &&
        (media.closest('slideshow-component') === this.slideshow || media.closest('.media-gallery__grid')));
  }

  #syncPlayback = () => {
    for (const media of this.#galleryVideos()) {
      if (media.closest('.media-gallery__grid')) {
        media.setGalleryPlayback?.(true, this.#gridVisibility.get(media) === true, false);
      } else {
        media.setGalleryPlayback?.(Boolean(this.#selectedSlide?.contains(media)), this.#selectedVisible, false);
      }
    }
  };

  #pauseGallery() {
    for (const media of this.#galleryVideos()) media.setGalleryPlayback?.(false, false, false);
  }

  #selectPlaybackSlide(slide) {
    if (!slide || slide === this.#selectedSlide) return;
    for (const media of this.#galleryVideos()) {
      if (!media.closest('.media-gallery__grid')) media.setGalleryPlayback?.(false, false, false);
    }
    this.#visibilityObserver?.disconnect();
    this.#selectedSlide = slide;
    this.#visibilityTarget = slide.querySelector('deferred-media') ?? slide;
    this.#selectedVisible = false;
    this.#syncPlayback();
    this.#visibilityObserver?.observe(this.#visibilityTarget);
  }

  #handleSlideSelect = (event) => {
    if (event.target !== this.slideshow) return;
    this.#selectPlaybackSlide(event.detail.slide);
  };

  disconnectedCallback() {
    this.#pauseGallery();
    this.#visibilityObserver?.disconnect();
    this.#gridObserver?.disconnect();
    this.#gridVisibility = new WeakMap();
    this.#selectedSlide = undefined;
    this.#visibilityTarget = undefined;
    super.disconnectedCallback();

    this.#controller.abort();
    this.#requestId++;
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    if (!event.promise) return;
    const requestId = ++this.#requestId;
    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || requestId !== this.#requestId || !detail?.html) return;
        const currentId = this.dataset.beskarProductId;
        if (currentId && detail.productId && !detail.newProduct && String(detail.productId) !== currentId) return;
        const targetId = String(detail.newProduct?.id || currentId || '');
        const galleries = [...detail.html.querySelectorAll('media-gallery')];
        const newMediaGallery = targetId
          ? galleries.find((gallery) => gallery.dataset.beskarProductId === targetId)
          : galleries[0];
        if (!newMediaGallery) return;
        // Same colour + same media: keep carousel position, zoom state and loaded images on size change.
        if (this.dataset.beskarColorKey &&
            this.dataset.beskarColorKey === newMediaGallery.dataset.beskarColorKey &&
            currentId === newMediaGallery.dataset.beskarProductId &&
            this.dataset.beskarMediaIds === newMediaGallery.dataset.beskarMediaIds) return;
        this.#pauseGallery();
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
